// src/store/slices/securityEmployeeQuestionSlice.js
import securityQuestionApi from 'api/securityQuestionApi';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import securityEmployeeQuestionApi from 'api/securityEmployeeQuestionApi';

/* ------------------------------------------------------------------ */
/* Async Thunks */
/* ------------------------------------------------------------------ */

export const fetchSecurityQuestions = createAsyncThunk(
  'securityQuestion/fetchSecurityQuestions',
  async ({ page = 1, pageSize = 10, search = '' }, { rejectWithValue }) => {
    try {
      const response = await securityEmployeeQuestionApi.getAll(page, pageSize, search);
      return response;
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to load security questions';
      return rejectWithValue(msg);
    }
  }
);

export const createSecurityQuestion = createAsyncThunk(
  'securityQuestion/createSecurityQuestion',
  async (data, { rejectWithValue }) => {
    try {
      const response = await securityEmployeeQuestionApi.create(data);
      return response;
    } catch (err) {
      const fieldErrors = err.response?.data;
      if (fieldErrors && typeof fieldErrors === 'object') {
        const messages = Object.entries(fieldErrors)
          .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors[0] : errors}`)
          .join(', ');
        return rejectWithValue(messages);
      }
      return rejectWithValue(err.response?.data?.detail || 'Failed to create question');
    }
  }
);

export const updateSecurityQuestion = createAsyncThunk(
  'securityQuestion/updateSecurityQuestion',
  async ({ id, ...updates }, { rejectWithValue }) => {
    try {
      const response = await securityEmployeeQuestionApi.update(id, updates);
      return response;
    } catch (err) {
      const fieldErrors = err.response?.data;
      if (fieldErrors && typeof fieldErrors === 'object') {
        const messages = Object.entries(fieldErrors)
          .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors[0] : errors}`)
          .join(', ');
        return rejectWithValue(messages);
      }
      return rejectWithValue(err.response?.data?.detail || 'Failed to update question');
    }
  }
);

export const deleteSecurityQuestion = createAsyncThunk(
  'securityQuestion/deleteSecurityQuestion',
  async (id, { rejectWithValue }) => {
    try {
      await securityEmployeeQuestionApi.delete(id);
      return id;
    } catch (err) {
      console.log(err);
      const msg = err.response?.data?.detail || 'Failed to delete question';
      return rejectWithValue(msg);
    }
  }
);

/* ------------------------------------------------------------------ */
/* Slice */
/* ------------------------------------------------------------------ */

const securityEmployeeQuestionSlice = createSlice({
  name: 'securityQuestion',
  initialState: {
    questions: [],
    pagination: { next: null, previous: null, count: 0, page_size: 10 },
    isLoading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      /* FETCH */
      .addCase(fetchSecurityQuestions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSecurityQuestions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.questions = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchSecurityQuestions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      /* CREATE */
      .addCase(createSecurityQuestion.fulfilled, (state, action) => {
        state.questions.unshift(action.payload);
        state.pagination.count += 1;
      })

      /* UPDATE */
      .addCase(updateSecurityQuestion.fulfilled, (state, action) => {
        const idx = state.questions.findIndex((q) => q.id === action.payload.id);
        if (idx !== -1) state.questions[idx] = action.payload;
      })

      /* DELETE */
      .addCase(deleteSecurityQuestion.fulfilled, (state, action) => {
        state.questions = state.questions.filter((q) => q.id !== action.payload);
        state.pagination.count -= 1;
      });
  },
});

export const { clearError } = securityEmployeeQuestionSlice.actions;
export default securityEmployeeQuestionSlice.reducer;